'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { RefreshCw, X } from 'lucide-react';

import {
  canSnoozeClientUpdate,
  clearClientRefreshGate,
  isClientUpdateSnoozed,
  refreshClientWhenReady,
  snoozeClientUpdate,
  useVersionCheck,
} from '@/hooks/useVersionCheck';

export function VersionChecker() {
  const t = useTranslations('common');
  const versionState = useVersionCheck();
  const [now, setNow] = useState(() => Date.now());
  const [dismissedSignal, setDismissedSignal] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const noticeSignal = `${versionState.latestBuildId}:${versionState.refreshGateSignal}`;
  const visible = versionState.isStale && dismissedSignal !== noticeSignal
    && (!isClientUpdateSnoozed(now) || Boolean(versionState.refreshGateOpenedAt));

  useEffect(() => {
    if (!versionState.snoozedUntil) return;
    const delay = Math.max(0, versionState.snoozedUntil - Date.now());
    const timer = window.setTimeout(() => setNow(Date.now()), delay);
    return () => window.clearTimeout(timer);
  }, [versionState.snoozedUntil]);

  const handleSnooze = () => {
    if (snoozeClientUpdate()) {
      setNow(Date.now());
    }
    clearClientRefreshGate();
    setDismissedSignal(noticeSignal);
  };

  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refreshClientWhenReady();
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-3 left-3 right-3 z-[9998] flex max-w-[min(680px,calc(100vw-24px))] items-center gap-3 rounded-lg border border-accent-soft bg-white px-4 py-3 shadow-modal sm:left-1/2 sm:right-auto sm:-translate-x-1/2"
    >
      <RefreshCw className="h-4 w-4 shrink-0 text-accent-core" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium leading-5 text-hr-text-primary">
          {t('newVersionAvailable')}
        </div>
        <div className="mt-0.5 text-xs leading-4 text-hr-text-secondary">
          {t('refreshRequiredBody')}
        </div>
      </div>
      <button
        onClick={() => {
          void handleRefresh().catch(() => {
            // The button resets in finally; a failed refresh never blocks navigation.
          });
        }}
        disabled={isRefreshing}
        className="shrink-0 rounded-lg bg-accent-core px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-accent-hover disabled:cursor-wait disabled:opacity-70"
      >
        {isRefreshing ? t('processing') : t('refreshNow')}
      </button>
      {canSnoozeClientUpdate() ? (
        <button
          onClick={handleSnooze}
          disabled={isRefreshing}
          className="shrink-0 rounded-lg border border-hr-border px-3 py-1.5 text-xs font-medium text-hr-text-secondary transition-colors hover:border-hr-border-strong hover:text-hr-text-primary disabled:cursor-not-allowed disabled:opacity-60"
        >
          {t('refreshLater')}
        </button>
      ) : null}
      <button type="button" onClick={handleSnooze} aria-label={t('close')}
        className="shrink-0 rounded-md p-1 text-hr-text-secondary transition-colors hover:bg-hr-surface-subtle">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
